import { useMemo, useState, useEffect } from 'react';
import {
  Search,
  RotateCcw,
  Plus,
  Minus,
  ClipboardCheck,
  Info,
  ChevronDown,
  ChevronUp,
  Clock,
  CheckCircle2,
  XCircle,
  TrendingUp,
  TrendingDown,
  Wallet,
  Calendar,
  Filter,
  Scale,
  X,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { usePayroll } from '@/context/AppContext';
import { useToast } from '@/hooks/useToast';
import { fmtNum, formatDateShort } from '@/lib/payroll';

type SortMode = 'name' | 'net' | 'late' | 'days';

export function ReviewPage() {
  const {
    payrollData,
    rawById,
    adminAdj,
    setAdminAdj,
    setPayrollData,
  } = usePayroll();
  const { show } = useToast();
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortMode>('name');
  const [showOnlyWithDeductions, setShowOnlyWithDeductions] = useState(false);
  const [showOnlyWithAdj, setShowOnlyWithAdj] = useState(false);

  // Auto-expand first employee when data loads
  useEffect(() => {
    if (payrollData && payrollData.length > 0 && expanded.size === 0) {
      // Don't auto-expand — let user decide
    }
  }, [payrollData]);

  const filtered = useMemo(() => {
    if (!payrollData) return [];
    let r = [...payrollData];
    if (filter.trim()) {
      const q = filter.trim().toLowerCase();
      r = r.filter(
        (d) =>
          d.Name?.toLowerCase().includes(q) ||
          String(d.ID).includes(q) ||
          d.Job?.toLowerCase().includes(q),
      );
    }
    if (showOnlyWithDeductions) {
      // ✅ فلتر "لهم خصم" — أي موظف عنده خصم (تأخير/خروج مبكر/نسيان) أكبر من صفر
      r = r.filter((d: any) => {
        const late = Number(d['أيام جزاء التأخير'] || 0);
        const early = Number(d['أيام جزاء الخروج المبكر'] || 0);
        const miss = Number(d['أيام جزاء النسيان'] || 0);
        return late + early + miss > 0;
      });
    }
    if (showOnlyWithAdj) {
      r = r.filter((d: any) => Number(adminAdj[d.ID] || 0) !== 0);
    }
    if (sortBy === 'net') r.sort((a: any, b: any) => b['صافي القبض المستحق'] - a['صافي القبض المستحق']);
    else if (sortBy === 'name') r.sort((a: any, b: any) => String(a.Name).localeCompare(String(b.Name), 'ar'));
    else if (sortBy === 'late')
      r.sort((a: any, b: any) => (b['دقائق التأخير'] || 0) - (a['دقائق التأخير'] || 0));
    else if (sortBy === 'days')
      r.sort((a: any, b: any) => b['أيام الحضور المستحقة'] - a['أيام الحضور المستحقة']);
    return r;
  }, [payrollData, filter, sortBy, showOnlyWithDeductions, showOnlyWithAdj, adminAdj, rawById]);

  // ✅ التعديل على موظف — يحدّث adminAdj و payrollData معاً (live)
  const setAdj = (id: string, val: number) => {
    const newAdj = { ...adminAdj, [id]: val };
    setAdminAdj(newAdj);
    if (payrollData) {
      setPayrollData(
        payrollData.map((d: any) =>
          d.ID === id
            ? {
                ...d,
                'تعديلات إدارية (بالأيام)': val,
                'قيمة التعديلات الإدارية': val * (d['Salary / Day'] || 0),
                'صافي القبض المستحق':
                  (d['صافي قبل التعديل الإداري'] ?? 0) + val * (d['Salary / Day'] || 0),
              }
            : d,
        ),
      );
    }
  };

  const resetAdj = () => {
    if (!confirm('إلغاء كل التعديلات الإدارية وإعادة حساب الصافي الأساسي؟')) return;
    if (payrollData) {
      const newAdj: Record<string, number> = {};
      const reset = payrollData.map((d: any) => {
        newAdj[d.ID] = 0;
        const before = d['صافي قبل التعديل الإداري'] ?? d['صافي القبض المستحق'];
        return {
          ...d,
          'تعديلات إدارية (بالأيام)': 0,
          'قيمة التعديلات الإدارية': 0,
          'صافي القبض المستحق': before,
        };
      });
      setPayrollData(reset);
      setAdminAdj(newAdj);
      show('تم إلغاء كل التعديلات الإدارية', 'info');
    }
  };

  const toggleExpand = (id: string) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  const expandAll = () => setExpanded(new Set(filtered.map((d: any) => d.ID)));
  const collapseAll = () => setExpanded(new Set());

  // ✅ إحصائيات — الإجمالي الأصلي والإجمالي بعد التعديل الإداري
  const totalAdjDays = Object.values(adminAdj || {}).reduce((s: number, v: any) => s + Number(v || 0), 0);
  const totalAdjValue = payrollData?.reduce(
    (s: number, d: any) => s + (d['قيمة التعديلات الإدارية'] || 0),
    0,
  ) || 0;
  const totalNetOriginal = payrollData?.reduce(
    (s: number, d: any) => s + (d['صافي قبل التعديل الإداري'] ?? d['صافي القبض المستحق'] - (d['قيمة التعديلات الإدارية'] || 0)),
    0,
  ) || 0;
  const totalNetWithAdj = payrollData?.reduce(
    (s: number, d: any) => s + d['صافي القبض المستحق'],
    0,
  ) || 0;

  if (!payrollData) {
    return (
      <div className="space-y-5 animate-slide-up">
        <h2 className="text-2xl font-extrabold flex items-center gap-2">
          <ClipboardCheck size={22} className="text-sky-500" />
          مراجعة وتعديل المرتبات
        </h2>
        <Card className="text-center !p-12">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center mb-3">
            <Info size={32} className="text-slate-400" />
          </div>
          <p className="text-slate-600 dark:text-slate-300">ارفع الملفات واحسب المرتبات أولاً</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-extrabold flex items-center gap-2">
            <ClipboardCheck size={22} className="text-sky-500" />
            مراجعة وتعديل المرتبات
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            اضغط على أي موظف لعرض بصماته • عدّل القيمة الإدارية مباشرة من نفس الصف
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={resetAdj}>
            <RotateCcw size={14} /> إلغاء كل التعديلات
          </Button>
        </div>
      </div>

      {/* Info banner */}
      <Card accent="sky" padding="sm">
        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed flex items-start gap-2">
          <Info size={14} className="text-sky-500 shrink-0 mt-0.5" />
          <span>
            <strong className="text-sky-700 dark:text-sky-300">التعديلات الإدارية:</strong> القيمة
            الموجبة = إضافة أيام (مثلاً مكافأة). السالبة = خصم أيام. التعديل بيتحدّث فوراً في
            الإجماليات وفي كل الصفحات.
          </span>
        </p>
      </Card>

      {/* Summary bar — الإجمالي الأصلي vs الإجمالي بعد التعديل الإداري */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard
          icon={Scale}
          label="الإجمالي الأصلي"
          value={fmtNum(totalNetOriginal)}
          color="from-slate-500 to-slate-700"
          sub="بدون التعديلات الإدارية"
        />
        <SummaryCard
          icon={Wallet}
          label="الإجمالي بعد التعديل"
          value={fmtNum(totalNetWithAdj)}
          color="from-emerald-500 to-teal-500"
          sub="مع كل الزيادات/الخصومات الإدارية"
        />
        <SummaryCard
          icon={TrendingUp}
          label="تعديلات إدارية"
          value={`${totalAdjDays.toFixed(2)} يوم`}
          color="from-sky-500 to-blue-500"
          sub={`${fmtNum(totalAdjValue)} ج.م`}
        />
        <SummaryCard
          icon={Calendar}
          label="موظفون معدّلون"
          value={String(Object.values(adminAdj).filter((v: any) => Number(v) !== 0).length)}
          color="from-fuchsia-500 to-pink-500"
          sub={`من أصل ${payrollData.length}`}
        />
      </div>

      {/* Filters bar */}
      <Card padding="md">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="ابحث بالاسم أو الرقم أو الوظيفة..."
              className="pr-10 pl-10"
            />
            {/* ✅ زرار مسح البحث — بيظهر بس لو فيه نص */}
            {filter && (
              <button
                type="button"
                onClick={() => setFilter('')}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 flex items-center justify-center transition-colors"
                title="مسح البحث"
                aria-label="مسح البحث"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-xl p-1">
            {[
              { id: 'net', label: 'الصافي', icon: Wallet },
              { id: 'name', label: 'الاسم', icon: Filter },
              { id: 'days', label: 'الأيام', icon: Calendar },
              { id: 'late', label: 'التأخير', icon: Clock },
            ].map((s: any) => {
              const Icon = s.icon;
              return (
                <button
                  key={s.id}
                  onClick={() => setSortBy(s.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                    sortBy === s.id
                      ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-indigo-300 shadow'
                      : 'text-slate-500'
                  }`}
                >
                  <Icon size={12} /> {s.label}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => setShowOnlyWithDeductions(!showOnlyWithDeductions)}
            className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
              showOnlyWithDeductions
                ? 'bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300'
                : 'border-slate-300 dark:border-slate-700 text-slate-500'
            }`}
          >
            <TrendingDown size={12} className="inline ml-1" /> لهم خصم
          </button>
          <button
            onClick={() => setShowOnlyWithAdj(!showOnlyWithAdj)}
            className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
              showOnlyWithAdj
                ? 'bg-sky-100 dark:bg-sky-900/30 border-sky-300 dark:border-sky-700 text-sky-700 dark:text-sky-300'
                : 'border-slate-300 dark:border-slate-700 text-slate-500'
            }`}
          >
            <TrendingUp size={12} className="inline ml-1" /> معدّل فقط
          </button>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={expandAll}>
              <ChevronDown size={14} /> فتح الكل
            </Button>
            <Button variant="ghost" size="sm" onClick={collapseAll}>
              <ChevronUp size={14} /> إغلاق الكل
            </Button>
          </div>
        </div>
      </Card>

      {/* Employee accordion list */}
      <div className="space-y-2">
        {filtered.map((d: any, idx: number) => (
          <EmployeeRow
            key={d.ID}
            employee={d}
            rawDays={rawById[d.ID] || []}
            adj={Number(adminAdj[d.ID] || 0)}
            onAdjChange={(v) => setAdj(d.ID, v)}
            expanded={expanded.has(d.ID)}
            onToggle={() => toggleExpand(d.ID)}
            index={idx + 1}
          />
        ))}
        {filtered.length === 0 && (
          <Card className="text-center !p-12">
            <p className="text-slate-500">لا يوجد موظفين مطابقين للبحث</p>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ───────── Employee Row (accordion) ───────── */

function EmployeeRow({
  employee,
  rawDays,
  adj,
  onAdjChange,
  expanded,
  onToggle,
  index,
}: {
  employee: any;
  rawDays: any[];
  adj: number;
  onAdjChange: (v: number) => void;
  expanded: boolean;
  onToggle: () => void;
  index: number;
}) {
  const salaryPerDay = employee['Salary / Day'] || 0;
  const adjValue = adj * salaryPerDay;
  // ✅ الـ "صافي القبض المستحق" في payrollData بيتحدّث live في setAdj
  const net = employee['صافي القبض المستحق'];
  const present = rawDays.filter((x) => x.isPresent).length;
  const absent = rawDays.filter((x) => x.isAbsent).length;
  const totalOT = rawDays.reduce((s, x) => s + x.ot, 0);
  const totalLateMins = rawDays.reduce((s, x) => s + (x.lateDiffMins || 0), 0);
  const totalEarlyMins = rawDays.reduce((s, x) => s + (x.earlyDiffMins || 0), 0);
  const totalDed = employee['قيمة خصم التأخير'] + (employee['قيمة خصم الخروج المبكر'] || 0) + employee['قيمة خصم النسيان'];

  const init = (employee.Name?.charAt(0) || '?').toUpperCase();
  const colorHue = hashHue(employee.ID);

  return (
    <div
      className={`glass rounded-2xl overflow-hidden shadow-soft transition-all ${
        expanded ? 'ring-2 ring-sky-400/40' : ''
      } ${absent > 0 ? 'border-r-4 border-rose-400' : adj !== 0 ? 'border-r-4 border-sky-400' : ''}`}
    >
      {/* Main row (always visible) */}
      <div
        className={`p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors ${
          expanded ? 'bg-sky-50/40 dark:bg-sky-900/10' : ''
        }`}
        onClick={onToggle}
      >
        {/* Index + Avatar */}
        <div className="text-xs text-slate-400 font-mono w-8 text-center shrink-0">{index}</div>
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold shrink-0 shadow-sm"
          style={{ background: `linear-gradient(135deg, hsl(${colorHue}, 65%, 55%), hsl(${(colorHue + 40) % 360}, 65%, 45%))` }}
        >
          {init}
        </div>

        {/* Name + Job */}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-slate-800 dark:text-slate-100 truncate">{employee.Name}</div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {employee.Job} • ID: {employee.ID}
          </div>
        </div>

        {/* Quick stats */}
        <div className="hidden md:flex items-center gap-3 shrink-0">
          <div className="text-center">
            <div className="text-xs text-slate-400">يومية</div>
            <div className="text-xs font-mono font-bold">{fmtNum(salaryPerDay)}</div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-400">أيام</div>
            <div className="text-xs font-bold text-indigo-600 dark:text-indigo-400">
              {employee['أيام الحضور المستحقة']}
            </div>
          </div>
          <div className="text-center">
            <div className="text-xs text-slate-400">إضافي</div>
            <div className="text-xs font-bold text-sky-600 dark:text-sky-400">
              {(totalOT / 8).toFixed(2)} <span className="text-[10px] opacity-70">يوم</span>
            </div>
          </div>
          {/* ✅ مجموع الخصم (بالأيام) — قبل الغياب */}
          {(employee['أيام جزاء التأخير'] + (employee['أيام جزاء الخروج المبكر'] || 0) + employee['أيام جزاء النسيان']) > 0 && (
            <div className="text-center">
              <div className="text-xs text-slate-400">الخصم</div>
              <div className="text-xs font-bold text-amber-600 dark:text-amber-400">
                {(employee['أيام جزاء التأخير'] + (employee['أيام جزاء الخروج المبكر'] || 0) + employee['أيام جزاء النسيان']).toFixed(2)} <span className="text-[10px] opacity-70">يوم</span>
              </div>
            </div>
          )}
          {absent > 0 && (
            <div className="text-center">
              <div className="text-xs text-slate-400">غياب</div>
              <div className="text-xs font-bold text-rose-600 dark:text-rose-400">{absent}</div>
            </div>
          )}
        </div>

        {/* Admin adj input */}
        <div
          className="flex items-center gap-1.5 shrink-0 bg-gradient-to-l from-sky-50 to-indigo-50 dark:from-sky-900/20 dark:to-indigo-900/20 p-1.5 rounded-xl border border-sky-200/60 dark:border-sky-800/40"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => onAdjChange(+(adj - 0.25).toFixed(2))}
            className="w-7 h-7 rounded-lg bg-white dark:bg-slate-800 text-rose-600 dark:text-rose-400 flex items-center justify-center hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors shadow-sm"
            aria-label="إنقاص"
            title="إنقاص 0.25 يوم"
          >
            <Minus size={14} />
          </button>
          <input
            type="number"
            step="0.25"
            dir="ltr"
            value={adj}
            onChange={(e) => onAdjChange(parseFloat(e.target.value) || 0)}
            onClick={(e) => e.stopPropagation()}
            onWheel={(e) => e.currentTarget.blur()}
            className="w-16 px-2 py-1 rounded-lg border border-sky-300 dark:border-sky-700 bg-white dark:bg-slate-800 text-center font-bold text-sky-700 dark:text-sky-300 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/30"
            placeholder="0"
          />
          <button
            type="button"
            onClick={() => onAdjChange(+(adj + 0.25).toFixed(2))}
            className="w-7 h-7 rounded-lg bg-white dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 flex items-center justify-center hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors shadow-sm"
            aria-label="زيادة"
            title="زيادة 0.25 يوم"
          >
            <Plus size={14} />
          </button>
          <div className="hidden sm:block text-[10px] text-slate-500 dark:text-slate-400 mr-1">يوم</div>
        </div>

        {/* Net total */}
        <div className="text-left shrink-0 min-w-[110px]" onClick={(e) => e.stopPropagation()}>
          <div className="text-[10px] text-slate-400">الصافي بعد التعديل</div>
          <div
            className={`font-mono font-bold text-base ${
              net > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500'
            }`}
          >
            {fmtNum(net)}
          </div>
          {adj !== 0 && (
            <div
              className={`text-[10px] font-bold ${
                adjValue > 0
                  ? 'text-sky-600 dark:text-sky-400'
                  : 'text-rose-600 dark:text-rose-400'
              }`}
            >
              {adjValue > 0 ? '+' : ''}
              {fmtNum(adjValue)} ({adj > 0 ? '+' : ''}
              {adj.toFixed(2)})
            </div>
          )}
        </div>

        {/* Expand chevron */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shrink-0"
          aria-label={expanded ? 'إغلاق' : 'فتح'}
        >
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {/* Expanded attendance details */}
      {expanded && (
        <div className="border-t border-slate-200/60 dark:border-slate-800 bg-white/40 dark:bg-slate-900/40 animate-slide-down">
          {/* Quick stats row */}
          <div className="px-4 py-3 grid grid-cols-2 md:grid-cols-6 gap-2 border-b border-slate-200/40 dark:border-slate-800/40">
            <Stat icon={CheckCircle2} label="حضور" value={present} variant="emerald" />
            <Stat icon={XCircle} label="غياب" value={absent} variant="rose" />
            <Stat icon={Clock} label={`تأخير (${totalLateMins} د)`} value={`${employee['أيام جزاء التأخير']?.toFixed(2) || '0'} يوم`} variant="amber" />
            <Stat icon={Clock} label={`خروج (${totalEarlyMins} د)`} value={`${(employee['أيام جزاء الخروج المبكر'] || 0).toFixed(2)} يوم`} variant="amber" />
            <Stat icon={TrendingUp} label="إضافي" value={`${totalOT} س`} variant="sky" />
            <Stat icon={Wallet} label="إجمالي خصم" value={fmtNum(totalDed)} variant="rose" />
          </div>

          {/* Attendance table */}
          <div className="overflow-x-auto max-h-[440px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className="text-right text-[10px] font-bold uppercase tracking-wider text-white bg-gradient-to-l from-sky-600 to-indigo-600">
                  <th className="px-2 py-2 whitespace-nowrap">التاريخ</th>
                  <th className="px-2 py-2 whitespace-nowrap">اليوم</th>
                  <th className="px-2 py-2 whitespace-nowrap">دخول<br/>فعلي</th>
                  <th className="px-2 py-2 whitespace-nowrap">خروج<br/>فعلي</th>
                  <th className="px-2 py-2 whitespace-nowrap">تأخير<br/>(د)</th>
                  <th className="px-2 py-2 whitespace-nowrap bg-rose-700/40">جزاء<br/>تأخير<br/>(ي)</th>
                  <th className="px-2 py-2 whitespace-nowrap">مبكر<br/>(د)</th>
                  <th className="px-2 py-2 whitespace-nowrap bg-amber-700/40">جزاء<br/>خروج<br/>(ي)</th>
                  <th className="px-2 py-2 whitespace-nowrap">نسيان<br/>(ي)</th>
                  <th className="px-2 py-2 whitespace-nowrap">حالة</th>
                  <th className="px-2 py-2 whitespace-nowrap">إضافي<br/>(س)</th>
                  <th className="px-2 py-2 whitespace-nowrap bg-indigo-700/40">جزاء<br/>نهائي</th>
                </tr>
              </thead>
              <tbody>
                {rawDays.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="text-center py-6 text-slate-400">
                      لا توجد بصمات لهذا الموظف
                    </td>
                  </tr>
                ) : (
                  rawDays.map((d: any, i: number) => {
                    const dateObj = d.date ? new Date(d.date) : null;
                    const dayName =
                      dateObj && !isNaN(dateObj.getTime())
                        ? dateObj.toLocaleDateString('ar-EG', { weekday: 'short', numberingSystem: 'latn' })
                        : '';
                    const lateDay = d.lateDed || 0;
                    const earlyDay = d.earlyDed || 0;
                    const missDay = d.missDed || 0;
                    const finalDed = lateDay + earlyDay + missDay;
                    return (
                      <tr
                        key={i}
                        className={`border-b border-slate-100 dark:border-slate-800 hover:bg-sky-50/30 dark:hover:bg-sky-900/10 transition-colors ${
                          d.isAbsent ? 'bg-rose-50/40 dark:bg-rose-900/10' : ''
                        }`}
                      >
                        <td className="px-2 py-2 whitespace-nowrap font-bold text-slate-700 dark:text-slate-200">
                          {formatDateShort(d.date)}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-500 text-[10px]">{dayName}</td>
                        <td
                          className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${
                            d.clockIn === '--'
                              ? 'text-slate-400'
                              : 'text-indigo-700 dark:text-indigo-300'
                          }`}
                        >
                          {d.clockIn === '--' ? '—' : d.clockIn}
                        </td>
                        <td
                          className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${
                            d.clockOut === '--'
                              ? 'text-slate-400'
                              : 'text-indigo-700 dark:text-indigo-300'
                          }`}
                        >
                          {d.clockOut === '--' ? '—' : d.clockOut}
                        </td>
                        <td className="px-2 py-2 text-center text-rose-600 dark:text-rose-400 font-bold">
                          {d.lateDiffMins || '—'}
                        </td>
                        <td className="px-2 py-2 text-center text-rose-700 dark:text-rose-300 font-mono font-bold bg-rose-50/40 dark:bg-rose-900/10">
                          {lateDay > 0 ? lateDay.toFixed(3) : '—'}
                        </td>
                        <td className="px-2 py-2 text-center text-amber-600 dark:text-amber-400 font-bold">
                          {d.earlyDiffMins || '—'}
                        </td>
                        <td className="px-2 py-2 text-center text-amber-700 dark:text-amber-300 font-mono font-bold bg-amber-50/40 dark:bg-amber-900/10">
                          {earlyDay > 0 ? earlyDay.toFixed(3) : '—'}
                        </td>
                        <td className="px-2 py-2 text-center text-orange-700 dark:text-orange-300 font-mono font-bold">
                          {missDay > 0 ? (
                            <span title={`دخول: ${d.missIn} / خروج: ${d.missOut}`}>
                              {missDay.toFixed(2)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-2 py-2 text-center">
                          <StatusBadge
                            isAbsent={d.isAbsent}
                            status={d.status}
                            dayName={dayName}
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          {d.ot > 0 ? (
                            <Badge variant="sky">{d.ot}</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center font-bold text-indigo-700 dark:text-indigo-300 font-mono bg-indigo-50/40 dark:bg-indigo-900/20">
                          {finalDed > 0 ? finalDed.toFixed(3) : '—'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ✅ ترجمة حالة اليوم للعربي + عرض اسم اليوم
const STATUS_AR: Record<string, { label: string; variant: 'emerald' | 'rose' | 'amber' | 'sky' | 'slate' }> = {
  Present: { label: 'حضور', variant: 'emerald' },
  Absent: { label: 'غياب', variant: 'rose' },
  Late: { label: 'متأخر', variant: 'amber' },
  'Late In': { label: 'تأخر', variant: 'amber' },
  'Early Leave': { label: 'خرج مبكر', variant: 'amber' },
  Normal: { label: 'حضور', variant: 'emerald' },
  Leave: { label: 'إجازة', variant: 'sky' },
  Holiday: { label: 'عطلة', variant: 'sky' },
  Weekend: { label: 'عطلة', variant: 'sky' },
  Off: { label: 'راحة', variant: 'sky' },
  Rest: { label: 'راحة', variant: 'sky' },
  Sick: { label: 'مرضي', variant: 'sky' },
  'Sick Leave': { label: 'إجازة مرضية', variant: 'sky' },
};

function StatusBadge({
  isAbsent,
  status,
  dayName,
}: {
  isAbsent?: boolean;
  status?: string;
  dayName?: string;
}) {
  let label: string;
  let variant: 'emerald' | 'rose' | 'amber' | 'sky' | 'slate';

  if (isAbsent) {
    label = 'غياب';
    variant = 'rose';
  } else if (status && STATUS_AR[status]) {
    label = STATUS_AR[status].label;
    variant = STATUS_AR[status].variant;
  } else if (status && status.toLowerCase() === 'present') {
    label = 'حضور';
    variant = 'emerald';
  } else if (status && status.toLowerCase() === 'absent') {
    label = 'غياب';
    variant = 'rose';
  } else {
    label = status || '—';
    variant = 'slate';
  }

  return (
    <div className="flex flex-col items-center gap-0.5">
      <Badge variant={variant}>{label}</Badge>
      {dayName && (
        <span className="text-[9px] text-slate-500 dark:text-slate-400 font-bold">
          {dayName}
        </span>
      )}
    </div>
  );
}

function hashHue(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) % 360;
  }
  return h;
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  color,
  sub,
}: {
  icon: any;
  label: string;
  value: string;
  color: string;
  sub?: string;
}) {
  return (
    <div className={`rounded-2xl p-4 text-white bg-gradient-to-br ${color} shadow-lg`}>
      <div className="flex items-center gap-2 mb-1 opacity-90">
        <Icon size={14} />
        <span className="text-[11px] font-bold">{label}</span>
      </div>
      <div className="text-xl font-black font-mono truncate">{value}</div>
      {sub && <div className="text-[10px] opacity-80 mt-0.5">{sub}</div>}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  variant,
}: {
  icon: any;
  label: string;
  value: any;
  variant: 'emerald' | 'rose' | 'amber' | 'sky';
}) {
  const colors = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose: 'text-rose-700 dark:text-rose-300',
    amber: 'text-amber-700 dark:text-amber-300',
    sky: 'text-sky-700 dark:text-sky-300',
  };
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-white/60 dark:bg-slate-800/60">
      <Icon size={14} className={colors[variant]} />
      <div className="min-w-0">
        <div className={`text-sm font-bold truncate ${colors[variant]}`}>{value}</div>
        <div className="text-[10px] text-slate-500 truncate">{label}</div>
      </div>
    </div>
  );
}
